import { HandBack, record } from '../ability/context.js';
import { ftCampaignTimeAddSeconds, ftCampaignTimeSubtractSeconds, type CampaignClockCols, type CampaignCalendarEmbed } from '../../shared/campaign-clock.js';
import { pendingOocRounds } from './ooc-round-pass.js';

interface Span { rounds: number; clock: CampaignClockCols; fast_forward: boolean; legacy?: boolean }
export interface PendingSpanRemainder { rounds: number; segments: Span[] }

/** Raw checkpoint facts and older whole-round debts share one browser planner.
 * Gaps and fast-forwards retain their boundaries; no absent encounter is simulated. */
export function planPendingTimeSpan(debt: Record<string, unknown>, fallback: CampaignClockCols, calendar: CampaignCalendarEmbed, rosterSize: number) {
  if (!calendar.months.length) throw new HandBack('pending-time:calendar-not-held');
  const source = Array.isArray(debt.segments) && debt.segments.length ? debt.segments
    : [{ rounds: debt.rounds ?? 1, legacy: true }];
  const segments: Span[] = [];
  for (const raw of source) {
    const saved = record(raw);
    if (!saved) throw new HandBack('pending-time:span-invalid');
    const heldClock = record(saved.clock);
    if (saved.elapsed_seconds !== undefined && !heldClock) throw new HandBack('pending-time:clock-invalid');
    const clock = (heldClock ?? fallback) as unknown as CampaignClockCols;
    if (!(['year', 'month', 'day', 'hour', 'minute', 'second'] as const).every(key => Number.isFinite(clock[key])))
      throw new HandBack('pending-time:clock-invalid');
    const fastForward = saved.fast_forward === true;
    let rounds = Number(saved.rounds ?? 0);
    if (saved.elapsed_seconds !== undefined) {
      const seconds = Number(saved.elapsed_seconds);
      if (!Number.isSafeInteger(seconds) || seconds < 0) throw new HandBack('pending-time:span-invalid');
      const from = ftCampaignTimeSubtractSeconds(clock, calendar, seconds);
      rounds = Math.floor((from.second % 6 + seconds) / 6);
    }
    if (!Number.isSafeInteger(rounds) || rounds < 0) throw new HandBack('pending-time:span-invalid');
    if (rounds === 0) continue;
    // Older whole-round debt ends at the held clock, including a due status's exact deadline.
    const end = fastForward || saved.legacy ? clock : ftCampaignTimeSubtractSeconds(clock, calendar, clock.second % 6);
    const span: Span = { rounds, clock: end, fast_forward: fastForward, ...(saved.legacy ? { legacy: true } : {}) };
    const previous = segments[segments.length - 1];
    if (previous && !previous.fast_forward && !fastForward && !previous.legacy && !span.legacy) {
      const next = ftCampaignTimeAddSeconds(previous.clock, calendar, rounds * 6);
      if ((Object.keys(end) as Array<keyof CampaignClockCols>).every(key => end[key] === next[key])) {
        span.rounds += previous.rounds;
        segments[segments.length - 1] = span;
        continue;
      }
    }
    segments.push(span);
  }
  const first = segments[0];
  const rounds = first ? pendingOocRounds(rosterSize, first.rounds) : 0;
  const clock = first ? ftCampaignTimeSubtractSeconds(first.clock, calendar, (first.fast_forward ? rounds : first.rounds) * 6) : fallback;
  const remaining: PendingSpanRemainder = { rounds: segments.reduce((n, span) => n + span.rounds, 0) - rounds,
    segments: first && first.rounds > rounds ? [{ ...first, rounds: first.rounds - rounds }, ...segments.slice(1)] : segments.slice(1) };
  return { rounds, clock, fastForward: first?.fast_forward === true, remaining };
}
